import { AnimatePresence, motion } from "framer-motion";
import { ReactNode } from "react";
import BlurryBackground from "./BlurryBackground";
import { keepOnMainThread } from "../helpers/motion";

type Props = {
    children: ReactNode;
    image?: string;
    video?: string;
    modalType?: string;
    showCheck?: boolean;
}

const ModalPage = ({ children, image, video, modalType, showCheck }: Props) => {
    const isMiddleSection = modalType === 'middleSection';

    // Alt her som animerer opacity har onUpdate={keepOnMainThread} — se
    // helpers/motion.ts (ClickUp: «Flicker i søknadsportalen»). Sirkelen tones
    // ikke inn i det hele tatt; den blinket bort når inntoningen var ferdig.
    return (
        <motion.div
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            onUpdate={keepOnMainThread}
            className="section d-flex justify-content-center align-items-center h-100"
        >
            <div className="bg-circles position-fixed overflow-hidden w-100 h-100">
                <BlurryBackground />

                {video ? (
                    <div className="bg-circles-video bg-circles-media position-absolute bankid-login-bg bg-white rounded-pill d-flex overflow-hidden">
                        {!isMiddleSection && (
                            <video 
                                className="w-100 h-100" 
                                style={{ 
                                    objectFit: 'cover',
                                    borderRadius: 'inherit' // Add this line
                                }} 
                                src={video} 
                                autoPlay 
                                loop 
                                muted 
                                playsInline
                            ></video>
                        )}
                    </div>
                ) : (
                    <motion.div
                        key="circle-background"
                        initial={false}
                        animate={{ opacity: 1 }}
                        className="bg-circles-img bg-circles-media position-absolute bankid-login-bg bg-white rounded-pill d-flex overflow-hidden"
                    >
                        <img className="w-100 h-100" style={{ objectFit: 'cover' }} src={image} ></img>
                    </motion.div>
                )}
            </div>

            <div className="position-absolute h-100 w-100" style={{ maxHeight: '100vh', paddingBottom: window.innerWidth >= 768 ? '2rem' : '0' }}>
                <AnimatePresence mode="wait">
                    <motion.div
                        key="draft-choice"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: isMiddleSection ? 0.5 : 0, delay: isMiddleSection ? 0.15 : 0 }}
                        onUpdate={keepOnMainThread}
                        className="container-fluid container-md bankid-login-bg-wrap h-100 px-0 px-md-3"
                    >
                        <div className={`row justify-content-center ${!video && !image ? 'align-items-center' : 'align-items-end'} w-100 h-100`}>
                            <div className={`col-12 col-md-10 col-lg-8 col-xl-6 d-flex flex-column justify-content-center px-0 px-md-3`}>
                                <div className="bg-body rounded-0 rounded-md-1 mb-md-5">
                                    {showCheck && <CheckAnimation />}
                                    <div className="text-center px-3 px-lg-5 py-4 d-flex flex-column justify-content-center h-100 gap-3">
                                        {children}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>
        </motion.div >
    )
}

export const CheckAnimation = () => {
    const classes = {
        width: 86,
        height: 86,
        borderRadius: "50%",
        background: "var(--bs-success)",
    }

    return (
        <div className="d-flex flex-column justify-content-end mb-2 flex-1" >
            <div className="d-flex justify-content-center">
                <div className="position-relative">
                    <motion.div
                        style={{
                            zIndex: 1,
                            ...classes
                        }}
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 0.25, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.15, ease: [0, 0.71, 0.2, 1.01] }}
                        onUpdate={keepOnMainThread}
                    />
                    <motion.div
                        style={{
                            zIndex: 2,
                            position: 'absolute',
                            top: 0,
                            ...classes
                        }}
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 0.5, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.25, ease: [0, 0.71, 0.2, 1.01] }}
                        onUpdate={keepOnMainThread}
                    />
                    <motion.div
                        style={{
                            zIndex: 3,
                            position: 'absolute',
                            top: 0,
                            ...classes
                        }}
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.40, ease: [0, 0.71, 0.2, 1.01] }}
                        onUpdate={keepOnMainThread}
                    />
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="#FFFFFF"
                        width={60}
                        height={60}
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 4
                        }}
                    >
                        <motion.path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: 1, opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.7, ease: [0.42, 0, 0.58, 1] }}
                            d="M4.5 12.75l6 6 9-13.5"
                        />
                    </svg>
                    <motion.div
                        style={{
                            zIndex: 3,
                            position: 'absolute',
                            top: 0,
                            ...classes
                        }}
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: [0, 0.25, 0], scale: [0.5, 1.25, 1] }}
                        transition={{ duration: 1.5, delay: 1, ease: [0, 0.71, 0.2, 1.01] }}
                        onUpdate={keepOnMainThread}
                    />
                </div>
            </div>
        </div>
    );
}

export default ModalPage;